import { useQuery } from '@tanstack/react-query';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircle20Filled, PeopleTeam20Regular } from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { useT } from '../i18n';
import { usePermission } from '../hooks/usePermission';

const useStyles = makeStyles({
  root: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge, marginBottom: tokens.spacingVerticalL,
  },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  ranking: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginLeft: tokens.spacingHorizontalXL },
  rankingRow: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
});

interface AdoptionWeekCount {
  count: number;
  since: string;
}

interface AdoptionRankingEntry {
  userId: string;
  name: string;
  count: number;
}

interface AdoptionTeamWeekCount {
  ranking: AdoptionRankingEntry[];
  since: string;
}

/**
 * Contador privado de adopción semanal — cada usuario ve siempre solo su propio
 * conteo. Quien tiene `operations:supervise` ve además un ranking de equipo
 * (gap declarado de quick-win UX Feature 1, resuelto 2026-10-27), separado y
 * opcional — el contador individual nunca deja de ser privado por defecto.
 * Derivado en vivo, sin persistir estado, mismo patrón que OnboardingChecklist.
 */
export default function AdoptionCounter({ tenantId }: { tenantId: string }) {
  const s = useStyles();
  const t = useT();
  const canSupervise = usePermission('operations:supervise');

  const { data } = useQuery<AdoptionWeekCount>({
    queryKey: ['adoptionMyWeekCount', tenantId],
    queryFn: async () => (await apiClient.get(`/api/adoption/my-week-count`)).data,
  });

  const { data: team } = useQuery<AdoptionTeamWeekCount>({
    queryKey: ['adoptionTeamWeekCount', tenantId],
    queryFn: async () => (await apiClient.get(`/api/adoption/team-week-count`)).data,
    enabled: canSupervise,
  });

  if (!data) return null;

  return (
    <div className={s.root}>
      <div className={s.row}>
        <CheckmarkCircle20Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
        <Text>
          {data.count === 1
            ? t.adoption.countSingular.replace('{count}', String(data.count))
            : t.adoption.countPlural.replace('{count}', String(data.count))}
        </Text>
      </div>
      {canSupervise && team && team.ranking.length > 0 && (
        <div className={s.row} style={{ alignItems: 'flex-start' }}>
          <PeopleTeam20Regular style={{ color: tokens.colorNeutralForeground2, marginTop: tokens.spacingVerticalXXS }} />
          <div className={s.ranking}>
            <Text weight="semibold" size={200}>{t.adoption.teamRankingTitle}</Text>
            {team.ranking.map((entry) => (
              <div key={entry.userId} className={s.rankingRow}>
                <Text size={200}>{entry.name}</Text>
                <Text size={200}>
                  {entry.count === 1
                    ? t.adoption.countSingular.replace('{count}', String(entry.count))
                    : t.adoption.countPlural.replace('{count}', String(entry.count))}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
